package org.vreo.devicehub.features.settings.presentation.settings

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.settingsRepository.SettingsRepository
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.domain.device.device.anprDeviceSettings.AnprDeviceSettings
import org.vreo.devicehub.features.core.domain.device.device.anprDeviceSettings.AnprDeviceUpdateReleaseType
import org.vreo.devicehub.features.core.domain.device.singleShootAutoFocus.SingleShotAutoFocusConfig
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoConfig
import org.vreo.devicehub.features.core.domain.device.videoCameraConfig.VideoFrameRotation
import org.vreo.devicehub.features.core.presentation.stingInputToFloatFormatter.validateAndFormatAmount
import org.vreo.devicehub.features.core.presentation.toUiText

/**
 * Settings view model, manages state of device's settings fields
 *
 * @param authentication target device authentication
 * @param repository device settings access repository for accessing remote device date
 */
class SettingsViewModel(
    private val authentication: DeviceAuthentication,
    private val repository: SettingsRepository,
    isAdmin: Boolean,
) : ViewModel() {
    private val _state = MutableStateFlow(SettingsState())
    val state = _state
        .onStart {
            if (authentication.deviceId.isNotBlank() && authentication.userToken.isNotBlank()) {
                _state.update {
                    it.copy(
                        isDeviceActive = authentication.isDeviceActive,
                        isAdmin = isAdmin
                    )
                }
                getDeviceConfig()
            }
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * Handles video control actions.
     *
     * @param action action to handle
     */
    fun onAction(action: SettingsActions) {
        when (action) {
            is SettingsActions.OnClearError -> _state.update { it.copy(error = null) }

            is SettingsActions.OnGetDeviceConfigClicked -> getDeviceConfig()

            is SettingsActions.OnSaveClicked -> saveConfig()
            
            // Zoom and focus settings
            is SettingsActions.OnZoomPositionChanged -> updateZoom(action.value)

            is SettingsActions.OnFocusPositionChanged -> updateFocus(action.value)

            is SettingsActions.OnSingleShotAutofocusClicked -> setSingleShotAutoFocus()

            // Image settings
            is SettingsActions.OnFrameRotationSelected -> _state.update { it.copy(selectedFrameRotationIndex = action.index) }

            is SettingsActions.OnBrightnessChanged -> updateTargetBrightness(action.value)

            is SettingsActions.OnExposureTimeChanged -> updateExposureTime(action.value, action.isMax)

            is SettingsActions.OnGainChanged -> updateGain(action.value, action.isMax)

            // Device settings
            is SettingsActions.OnUpdateReleaseTypeSelected -> _state.update { it.copy(selectedUpdateReleaseTypeIndex = action.index) }
        }
    }

    /**
     * Get device config from the repository and update the state with retrieved data on success, else
     * display an error.
     */
    private fun getDeviceConfig() = viewModelScope.launch {
        requestDeviceConfiguration()
    }

    /**
     * Send a request to a target device to request device's configuration
     */
    private suspend fun requestDeviceConfiguration() {
        _state.update { it.copy(isLoading = true) }
        val currentState = _state.value

        // Check if device is active, if not display error
        if (!currentState.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )
            }
            return
        }

        // Get settings
        runCatching {
            // Get device settings
            var deviceSettingsResult = repository.getDeviceSettings(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            if (deviceSettingsResult is Result.Error) {
                deviceSettingsResult = Result.Success(AnprDeviceSettings(AnprDeviceUpdateReleaseType.STABLE))
            }

            // Get video config
            val videoConfigResult = repository.getVideoConfiguration(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            if (videoConfigResult is Result.Error) {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = videoConfigResult.error.toUiText()
                    )
                }
            }

            // Set ui fields using retrieved data
            if (deviceSettingsResult is Result.Success && videoConfigResult is Result.Success) {
                setAllFields(deviceSettingsResult.data, videoConfigResult.data)
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null
                    )
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Set all user configuration fields to provided video configuration
     *
     * @param settings device settings
     * @param videoConfig video configuration
     */
    private fun setAllFields(settings: AnprDeviceSettings, videoConfig: VideoConfig) {
        try {
            // Set video config fields
            val rotation = videoConfig.frameRotation.ordinal
            val brightens = validateAndFormatAmount(value = videoConfig.targetBrightness.toString())
            val exposureMax =
                validateAndFormatAmount(value = videoConfig.maxExposureTimeUs.toString())
            val exposureMini =
                validateAndFormatAmount(value = videoConfig.minExposureTimeUs.toString())
            val gainMax = validateAndFormatAmount(value = videoConfig.maxGain.toString())
            val gainMini = validateAndFormatAmount(value = videoConfig.minGain.toString())

            // Set zoom and focus
            val zoomEnable = (videoConfig.zoomMini != null && videoConfig.zoomMax != null)
            val zoom = if (zoomEnable) {
                validateAndFormatAmount(
                    value = videoConfig.zoom.toString(),
                    maxDecimalPlaces = 10,
                    minAmount = videoConfig.zoomMini,
                    maxAmount = videoConfig.zoomMax
                ).toFloatOrNull() ?: videoConfig.zoomMini
            } else {
                0f
            }

            val focusEnable = (videoConfig.focusMini != null && videoConfig.focusMax != null)
            val focus = if (focusEnable) {
                validateAndFormatAmount(
                    value = videoConfig.focus.toString(),
                    maxDecimalPlaces = 10,
                    minAmount = videoConfig.focusMini,
                    maxAmount = videoConfig.focusMax
                ).toFloatOrNull() ?: videoConfig.focusMini
            } else {
                0f
            }

            // Set update release type
            val updateTypeIndex = settings.updateType.ordinal

            _state.update {
                it.copy(
                    // Video config fields
                    selectedFrameRotationIndex = rotation,
                    brightness = brightens,
                    exposureTimeMax = exposureMax,
                    exposureTimeMini = exposureMini,
                    gainMax = gainMax,
                    gainMini = gainMini,

                    // Zoom
                    zoomEnabled = zoomEnable,
                    zoom = zoom,
                    zoomMini = videoConfig.zoomMini ?: 0f,
                    zoomMax = videoConfig.zoomMax ?: 0f,

                    // Focus
                    focusEnabled = focusEnable,
                    focus = focus,
                    focusMini = videoConfig.focusMini ?: 0f,
                    focusMax = videoConfig.focusMax ?: 0f,

                    // Update type
                    selectedUpdateReleaseTypeIndex = updateTypeIndex
                )
            }
        } catch (e: Exception) {
            throw e
        }
    }

    /**
     * Saves the current configuration.
     */
    private fun saveConfig() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }
        if (!_state.value.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )

            }
            return@launch
        }

        // Validate new configuration
        val newBrightness = validateBrightness()
        val (newExposureTimeMax, newExposureTimeMini) = validateExposure()
        val (newGainMax, newGainMini) = validateGain()

        // Save new configuration as local state
        _state.update {
            it.copy(
                brightness = newBrightness,
                exposureTimeMax = newExposureTimeMax,
                exposureTimeMini = newExposureTimeMini,
                gainMax = newGainMax,
                gainMini = newGainMini
            )
        }

        val currentState = _state.value


        // Get current device's settings and update required field
        val currentDeviceSettings = runCatching {
            val result = repository.getDeviceSettings(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            when (result) {
                is Result.Success -> {
                    result.data
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = result.error.toUiText()
                        )
                    }
                    return@launch
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.NOT_FOUND.toUiText()
                )
            }
            return@launch
        }

        val newDeviceSettings = try {
            currentDeviceSettings.copy(
                updateType = AnprDeviceUpdateReleaseType.entries[currentState.selectedUpdateReleaseTypeIndex]
            )
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
            return@launch
        }


        // Get current device's video settings and update required field
        val currenVideoSettings = runCatching {
            val result = repository.getVideoConfiguration(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            when (result) {
                is Result.Success -> {
                    result.data
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = result.error.toUiText()
                        )
                    }
                    return@launch
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.NOT_FOUND.toUiText()
                )
            }
            return@launch
        }

        val newVideoSettings = try {
            // Initial temp config with basic/essential fields
            val tempConfig = currenVideoSettings.copy(
                frameRotation = VideoFrameRotation.entries[currentState.selectedFrameRotationIndex],
                targetBrightness = currentState.brightness.toFloatOrNull() ?: 0f,
                maxExposureTimeUs = currentState.exposureTimeMax.toFloat(),
                minExposureTimeUs = currentState.exposureTimeMini.toFloat(),
                maxGain = currentState.gainMax.toFloat(),
                minGain = currentState.gainMini.toFloat()
            )

            // Apply zoom if required
            val tempConfigS2 = if (currentState.zoomEnabled) {
                tempConfig.copy(zoom = currentState.zoom)
            } else {
                tempConfig
            }

            // Apply focus if required
            if (currentState.focusEnabled) {
                tempConfigS2.copy(focus = currentState.focus)
            } else {
                tempConfigS2
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
            return@launch
        }

        // Send new configuration to device
        runCatching {
            // Send device settings
            if (currentState.isAdmin) {
                val deviceSettingsResult = repository.setDeviceSettings(
                    token = authentication.userToken,
                    deviceId = authentication.deviceId,
                    config = newDeviceSettings
                )

                when (deviceSettingsResult) {
                    is Result.Success -> {}

                    is Result.Error -> {
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = deviceSettingsResult.error.toUiText()
                            )
                        }
                        return@launch
                    }
                }
            }

            // Send video config
            val videoConfigResult = repository.setVideoConfiguration(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                config = newVideoSettings
            )

            when (videoConfigResult) {
                is Result.Success -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null
                        )
                    }
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = videoConfigResult.error.toUiText()
                        )
                    }
                }
            }

        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Updates the target brightness value.
     *
     * @param value new target brightness value
     */
    private fun updateTargetBrightness(value: String) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        try {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = null,
                    brightness = validateAndFormatAmount(value = value)
                )
            }

        } catch (_: IllegalArgumentException) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Updates the exposure time, both minimum and maximum values.
     *
     * @param value new exposure time value
     * @param isMax whether the exposure time is the maximum limit (true) or the minimum limit (false)
     */
    private fun updateExposureTime(value: String, isMax: Boolean) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        try {
            val updatedExposureTime = validateAndFormatAmount(value = value)

            if (isMax) {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null,
                        exposureTimeMax = updatedExposureTime
                    )
                }

            } else {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null,
                        exposureTimeMini = updatedExposureTime
                    )
                }
            }
        } catch (_: IllegalArgumentException) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
        } catch (_: IllegalArgumentException) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Updates the gain, both minimum and maximum values.
     *
     * @param value new gain value
     */
    private fun updateGain(value: String, isMax: Boolean) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        try {
            val updatedGain = validateAndFormatAmount(value = value)

            if (isMax) {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null,
                        gainMax = updatedGain
                    )
                }
            } else {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = null,
                        gainMini = updatedGain
                    )
                }
            }
        } catch (_: IllegalArgumentException) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Request the target device to focus automatically
     */
    private fun setSingleShotAutoFocus() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        val currentState = _state.value
        if (!currentState.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )
            }
            return@launch
        }

        // Send request
        val results = try {
            repository.setSingleShotAutoFocus(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                config = SingleShotAutoFocusConfig()
            )
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.INVALID_ARGUMENT.toUiText(),
                )
            }
            return@launch
        }

        // If autofocus is successful, request new configuration
        when (results) {
            is Result.Success -> {
                requestDeviceConfiguration()
            }

            is Result.Error -> {
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = results.error.toUiText()
                    )
                }
            }
        }
    }

    /**
     * Validates brightness value, clamp it to mini and max.
     *
     * @return validated brightness value
     */
    private fun validateBrightness(): String {
        var newBrightness = _state.value.brightness

        try {
            // Validate brightness value, clamp it to mini and max
            if (newBrightness.isNotEmpty()) {
                val actualValue = newBrightness.toFloat()

                if (actualValue > state.value.brightnessLimitMax) {
                    newBrightness = state.value.brightnessLimitMax.toString()
                } else if (actualValue < state.value.brightnessLimitMini) {
                    newBrightness = state.value.brightnessLimitMini.toString()
                }
            } else {
                newBrightness = state.value.brightnessLimitMax.toString()
            }
        } catch (_: Exception) {
            newBrightness = state.value.brightnessLimitMax.toString()
        }
        return newBrightness
    }

    /**
     * Validates exposure time, clamp it to mini and max.
     *
     * @return validated exposure times as a pair of strings (max, min)
     */
    private fun validateExposure(): Pair<String, String> {
        var newExposureTimeMax = state.value.exposureTimeMax
        var newExposureTimeMini = state.value.exposureTimeMini

        if (newExposureTimeMax.isEmpty()) {
            newExposureTimeMax = state.value.exposureTimeLimitMax.toString()
        }
        if (newExposureTimeMini.isEmpty()) {
            newExposureTimeMini = state.value.exposureTimeLimitMini.toString()
        }
        // Validate max exposure time value, needs to be greater than mini and clamp it to max
        try {
            val actualValueMax = newExposureTimeMax.toFloat()
            val actualValueMini = newExposureTimeMini.toFloat()

            if ((actualValueMax < actualValueMini) ||
                (actualValueMax > state.value.exposureTimeLimitMax)
            ) {
                newExposureTimeMax = state.value.exposureTimeLimitMax.toString()
            }
        } catch (_: Exception) {
            newExposureTimeMax = state.value.exposureTimeLimitMax.toString()
        }

        // Validate min exposure time value, needs to be less than max and clamp it to min
        try {
            val actualValueMax = newExposureTimeMax.toFloat()
            val actualValueMini = newExposureTimeMini.toFloat()

            if ((actualValueMini > actualValueMax) ||
                (actualValueMini < state.value.exposureTimeLimitMini)
            ) {
                newExposureTimeMini = state.value.exposureTimeLimitMini.toString()
            }
        } catch (_: Exception) {
            newExposureTimeMini = state.value.exposureTimeLimitMini.toString()
        }

        return Pair(newExposureTimeMax, newExposureTimeMini)
    }

    /**
     * Validates gain, clamp it to mini and max.
     *
     * @return validated gain as a pair of strings (max, min)
     */
    private fun validateGain(): Pair<String, String> {
        var newGainMax = state.value.gainMax
        var newGainMini = state.value.gainMini

        if (newGainMax.isEmpty()) {
            newGainMax = state.value.gainLimitMax.toString()
        }
        if (newGainMini.isEmpty()) {
            newGainMini = state.value.gainLimitMini.toString()
        }

        // Validate max gain value, needs to be greater than mini and clamp it to max
        try {
            val actualValueMax = newGainMax.toFloat()
            val actualValueMini = newGainMini.toFloat()

            if ((actualValueMax < actualValueMini) ||
                (actualValueMax > state.value.gainLimitMax)
            ) {
                newGainMax = state.value.gainLimitMax.toString()
            }
        } catch (_: Exception) {
            newGainMax = state.value.gainLimitMax.toString()
        }

        // Validate min gain value, needs to be less than max and clamp it to min
        try {
            val actualValueMax = newGainMax.toFloat()
            val actualValueMini = newGainMini.toFloat()

            if ((actualValueMini > actualValueMax) ||
                (actualValueMini < state.value.gainLimitMini)
            ) {
                newGainMini = state.value.gainLimitMini.toString()
            }
        } catch (_: Exception) {
            newGainMini = state.value.gainLimitMini.toString()
        }

        return Pair(newGainMax, newGainMini)
    }

    /**
     * Update zoom value
     *
     * @param value value to update to
     */
    private fun updateZoom(value: String) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }
        try {
            val currentState = _state.value
            val validatedValue = validateAndFormatAmount(
                value = value,
                maxDecimalPlaces = 10,
                minAmount = currentState.zoomMini,
                maxAmount = currentState.zoomMax
            )

            val decimalValue = validatedValue.toFloatOrNull() ?: 0f

            _state.update {
                it.copy(
                    isLoading = false,
                    error = null,
                    zoom = decimalValue
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.FORMATTING.toUiText()
                )
            }
        }
    }

    /**
     * Update focus value
     *
     * @param value value to update to
     */
    private fun updateFocus(value: String) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }
        try {
            val currentState = _state.value
            val validatedValue = validateAndFormatAmount(
                value = value,
                maxDecimalPlaces = 10,
                minAmount = currentState.focusMini,
                maxAmount = currentState.focusMax
            )

            val decimalValue = validatedValue.toFloatOrNull() ?: 0f

            _state.update {
                it.copy(
                    isLoading = false,
                    error = null,
                    focus = decimalValue
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.FORMATTING.toUiText()
                )
            }
        }
    }

}